/* eslint-disable react-compiler/react-compiler */
/* eslint-disable react-hooks/exhaustive-deps */
import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import {
  Autocomplete,
  Box,
  CircularProgress,
  Grid,
  InputAdornment,
  Paper,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  useTheme,
} from "@mui/material";
import debounce from "lodash/debounce";
import isEqual from "lodash/isEqual";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useApi } from "@platform/api";
import { Alert, Button, TextField, Typography } from "@platform/design-system";
import { CURRENT_COVERAGES } from "@platform/rfp-core";

import { submitEvent } from "../../utils/tracking";
import { Loader } from "../../components";
import AddCurrentCoverages from "../../components/addCoverages/addCurrentCoverages";
import NewClientForm from "../../components/clientFormNew";
import { ROUTE_NAMES } from "../../constants";
import {
  CLIENT_FORM,
  CLIENT_REDUCER_ACTIONS,
  CLIENT_TYPES,
  IS_CLIENT_FORM_VALID,
  MARKET,
  ONE_DIGITAL,
} from "../../constants/client";
import { useAuth } from "../../hooks";
import {
  formatDate,
  getClientPayload,
  normalizeClientProducts,
  extractErrorMessage,
} from "../../utils";
import { ClientBtnSection, GradientBox } from "../RFP/components";

import ClientTiles from "./clientTiles";

const {
  CLIENT_TYPE,
  CLIENT_ID,
  NAME,
  CLIENT_SINCE,
  STATE,
  SITUS_STATE,
  BROKER_TYPE,
  BROKER_HAS_MULTIPLE_STRATEGIC_TIERS,
} = CLIENT_FORM;
const { CLEAN_CLIENT, UPDATE_CLIENT, UPDATE_CLIENT_FIELD } =
  CLIENT_REDUCER_ACTIONS;

const ClientCurrent = ({
  data: {
    states,
    brokerClientInfoConfig,
    brokerClientInfoAvailableOptions,
    oneDigitalOfficeLocations,
  },
  loading,
  client,
  setClient,
  error,
}) => {
  const location = useLocation();
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [coverages, setCoverages] = useState([]);
  const [clients, setClientList] = useState([]);
  const [isMissingClient, setIsMissingClient] = useState(false);
  const [listFetched, setListFetched] = useState(false);
  const [clientLoading, setClientLoading] = useState(false);
  const [clientError, setClientError] = useState(false);
  const [originalClient, setOriginalClient] = useState(null);
  const [autocompleteValue, setAutocompleteValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const navigate = useNavigate();
  const { brokerGroupInfo } = useAuth();
  const canAddClient =
    brokerGroupInfo?.type !== ONE_DIGITAL &&
    brokerGroupInfo?.variant !== MARKET;
  const axiosInstance = useApi();
  const clientId = client[CLIENT_ID];
  const brokerHasMultipleStrategicTiers =
    client[BROKER_HAS_MULTIPLE_STRATEGIC_TIERS];
  const clientBrokerType = client[BROKER_TYPE];

  useEffect(() => {
    if (debouncedSearchValue.length === 0) setClientList([]);
    else if (debouncedSearchValue.length > 2) {
      // Type in at least 3 characters -
      fetchClientList();
    }
  }, [debouncedSearchValue]);

  const debouncedSearch = useRef(
    debounce((value) => {
      setDebouncedSearchValue(value?.trim());
    }, 300),
  ).current;

  const fetchClientList = async () => {
    const brokerId = brokerGroupInfo?.id;
    setClientLoading(true);
    const searchURL = `/brokerportal/v1/brokers/${brokerId}/clients?q=${debouncedSearchValue}`;
    const clientList = await axiosInstance.get(searchURL);
    setClientList(clientList?.data?.results || []);
    setClientLoading(false);
    setListFetched(true);
  };

  const onClientSearched = (e) => {
    const newTerm = e.target.value || "";
    setAutocompleteValue(newTerm);
    debouncedSearch(newTerm);
  };

  // Type in at least 1 character and press enter
  const handleSearchKeyPress = (event) => {
    if (event.key === "Enter" && autocompleteValue.trim().length > 0) {
      setDebouncedSearchValue(autocompleteValue.trim());
      fetchClientList();
    }
  };

  const fetchClientDetails = async ({ name, id }) => {
    let clientDetail = {};
    setListFetched(false);
    setIsMissingClient(false);
    setAutocompleteValue(name);
    setClientLoading(true);
    try {
      setClientError(null);
      clientDetail = await axiosInstance.post(
        "/brokerportal/v1/clientDetails",
        {
          clientId: id,
        },
      );
      setOriginalClient(
        getClientPayload(
          {
            ...clientDetail.data,
            ...clientDetail.data.address,
            ...clientDetail.data.additionalInfo,
            [CLIENT_SINCE]: clientDetail?.data?.[CLIENT_SINCE],
          },
          brokerClientInfoConfig,
        ),
      );
    } catch (err) {
      setClientError(extractErrorMessage(err));
    } finally {
      setClient({
        type: UPDATE_CLIENT,
        value: {
          ...clientDetail.data,
          [CLIENT_SINCE]:
            clientDetail?.data?.[CLIENT_SINCE] &&
            new Date(clientDetail.data[CLIENT_SINCE]),
          [CLIENT_ID]: id,
          [CLIENT_TYPE]: CLIENT_TYPES.CURRENT_CLIENT,
          states,
        },
      });

      if (clientDetail?.data?.products) {
        const products = normalizeClientProducts(clientDetail?.data?.products);
        setCoverages(products);
      } else {
        setCoverages([]);
      }
      setClientLoading(false);
      setActiveStep(0);
    }
  };

  const handleIsMissingClient = () => {
    setAutocompleteValue("");
    setActiveStep(0);
    setClient({ type: CLEAN_CLIENT, value: CLIENT_TYPES.CURRENT_CLIENT });
    setCoverages([]);
    setIsMissingClient(true);
  };

  useEffect(() => {
    if (client?.clientId) {
      fetchClientDetails({ name: client.name, id: client.clientId });
    }
  }, []);

  const handleNextClientInfo = async () => {
    if (!client[IS_CLIENT_FORM_VALID]) return;

    const initPayload = getClientPayload(
      {
        ...client,
        [STATE]: client[STATE].abbreviation,
        [SITUS_STATE]: client[SITUS_STATE]?.abbreviation,
        [CLIENT_SINCE]: formatDate(client[CLIENT_SINCE]),
      },
      brokerClientInfoConfig,
    );

    if (isEqual(initPayload, originalClient)) {
      setActiveStep(1);
      return;
    }

    let isSuccess = false;
    try {
      setClientError(null);
      setClientLoading(true);
      const payload = {
        ...initPayload,
        isProspect: false,
      };
      if (clientId) {
        await axiosInstance.patch(
          `/brokerportal/v1/clients/${clientId}`,
          payload,
        );
      } else {
        const { data } = await axiosInstance.post(
          "/brokerportal/v1/clients",
          payload,
        );
        setClient({
          type: UPDATE_CLIENT_FIELD,
          name: CLIENT_ID,
          value: data.id,
        });
      }
      setOriginalClient(initPayload);
      isSuccess = true;
    } catch (err) {
      setClientError(extractErrorMessage(err));
    } finally {
      setClientLoading(false);
    }
    if (isSuccess) {
      setActiveStep(1);
    }
  };

  function handleAddCoverages(newCoverages) {
    submitEvent("Add Coverage Clicked", {
      coverage_type: coverages.map((c) => c.type),
    });
    setCoverages((old) => [...old, ...newCoverages]);
  }

  const renderAutoCompleteComponent = () => {
    const autoCompleteOptions = clients;
    const handleInputChange = onClientSearched;
    const helperText = "Search with at least 3 characters";

    return (
      <>
        <Autocomplete
          fullWidth
          inputValue={autocompleteValue}
          key="client-selection"
          open={listFetched}
          onBlur={() => setListFetched(false)}
          onChange={(e, client) => fetchClientDetails(client)}
          options={autoCompleteOptions}
          getOptionLabel={({ name }) => name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          loading={clientLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              size="sm"
              required
              placeholder="Search"
              inputProps={{
                ...params.inputProps,
                "data-testid": "client-search",
              }}
              onChange={handleInputChange}
              onKeyDown={handleSearchKeyPress}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: theme.palette.action.active }} />
                  </InputAdornment>
                ),
                endAdornment: clientLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null,
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.name}
            </li>
          )}
        />
        <Typography variant="caption">{helperText}</Typography>
      </>
    );
  };

  return (
    <ClientTiles client={client} setClient={setClient}>
      {loading && (
        <Loader mt={2} isCentered={false} sx={{ textAlign: "center" }} />
      )}
      {(error || clientError) && (
        <Box mb={2} width="inherit">
          <Alert severity="error">
            {extractErrorMessage(error || clientError)}
          </Alert>
        </Box>
      )}
      {clients && (
        <Grid component={Paper} p={5} mt={10}>
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Typography variant="header-large">
              Search for Existing Client
            </Typography>
            {canAddClient && (
              <Typography variant="body-large">
                Can’t find Current Client?{" "}
                <Button variant="tertiary" onClick={handleIsMissingClient}>
                  Add Current Client
                </Button>
              </Typography>
            )}
          </Box>
          {renderAutoCompleteComponent()}
          {(client[NAME] || isMissingClient) && (
            <>
              <Typography variant="display-small">
                {client[NAME] || "Client"}
              </Typography>
              <GradientBox mr={"auto"} />
            </>
          )}
          {(client[CLIENT_ID] || isMissingClient) && (
            <Stepper
              activeStep={activeStep}
              orientation="vertical"
              sx={{ mt: 2 }}
            >
              <Step>
                <StepLabel>
                  <ClientFormStepTitle isActive={activeStep === 0}>
                    Client Information
                  </ClientFormStepTitle>
                </StepLabel>
                <StepContent>
                  <ClientFormStep
                    client={client}
                    setClient={setClient}
                    clientLoading={clientLoading}
                    states={states}
                    handleNextClientInfo={handleNextClientInfo}
                    brokerClientInfoConfig={brokerClientInfoConfig}
                    brokerClientInfoAvailableOptions={
                      brokerClientInfoAvailableOptions
                    }
                    oneDigitalOfficeLocations={oneDigitalOfficeLocations}
                  />
                </StepContent>
              </Step>
              <Step>
                <StepLabel>
                  <ClientFormStepTitle isActive={activeStep === 1}>
                    Current Coverages
                  </ClientFormStepTitle>
                </StepLabel>
                <StepContent>
                  <AddCoveragesStep
                    brokerType={clientBrokerType}
                    brokerHasMultipleStrategicTiers={
                      brokerHasMultipleStrategicTiers
                    }
                    clientLoading={clientLoading}
                    coverages={coverages}
                    handleAddCoverages={handleAddCoverages}
                    clientId={clientId}
                    setActiveStep={setActiveStep}
                    clientType={client[CLIENT_TYPE]}
                    client={client}
                  />
                </StepContent>
              </Step>
            </Stepper>
          )}
        </Grid>
      )}
      <ClientBtnSection
        mainBtn="NEXT: RFP DETAILS"
        mainDisabled={!client[IS_CLIENT_FORM_VALID] || activeStep === 0}
        mainAction={() =>
          navigate(`/${ROUTE_NAMES.RFP}`, {
            state: {
              client,
              prevPath: location.pathname,
              [CURRENT_COVERAGES]: coverages,
            },
          })
        }
        secBtn="Cancel"
        secVariant="outlined"
        secHasIcon={false}
        secAction={() => navigate(`/${ROUTE_NAMES.RFPS}`)}
      />
    </ClientTiles>
  );
};

function AddCoveragesStep({
  clientLoading,
  brokerType,
  coverages,
  handleAddCoverages,
  clientId,
  setActiveStep,
  brokerHasMultipleStrategicTiers,
  clientType,
  client,
}) {
  return (
    <>
      {clientLoading ? (
        <Box position="relative" my={2}>
          <Loader />
        </Box>
      ) : (
        <AddCurrentCoverages
          brokerType={brokerType}
          brokerHasMultipleStrategicTiers={brokerHasMultipleStrategicTiers}
          coverages={coverages}
          onAddCoverages={handleAddCoverages}
          clientId={clientId}
          hideNonNACols
          showConnectedCarriers
          isAdmin={false}
          embeded
          showTitle={false}
          clientType={clientType}
          client={client}
          isConsultant={false}
        />
      )}
      <Button
        variant="secondary"
        startIcon={<ArrowUpward />}
        onClick={() => setActiveStep(0)}
        disabled={clientLoading}
        sx={{ mt: 3 }}
      >
        back to client information
      </Button>
    </>
  );
}

function ClientFormStepTitle({ children, isActive }) {
  return (
    <Typography variant={isActive ? "header-large" : "body-large"}>
      {isActive ? children : <strong>{children}</strong>}
    </Typography>
  );
}

function ClientFormStep({
  client,
  setClient,
  clientLoading,
  states,
  handleNextClientInfo,
  brokerClientInfoConfig,
  brokerClientInfoAvailableOptions,
  oneDigitalOfficeLocations,
}) {
  const handleChange = ({ target }) => {
    const { value, name } = target;
    setClient({
      type: UPDATE_CLIENT_FIELD,
      name,
      value,
    });
  };

  return (
    <>
      {clientLoading ? (
        <Loader mt={2} isCentered={false} sx={{ textAlign: "center" }} />
      ) : (
        <>
          <NewClientForm
            client={client}
            states={states}
            onChange={handleChange}
            brokerClientInfoConfig={brokerClientInfoConfig}
            brokerClientInfoAvailableOptions={brokerClientInfoAvailableOptions}
            oneDigitalOfficeLocations={oneDigitalOfficeLocations}
          />
          <Button
            variant="secondary"
            startIcon={<ArrowDownward />}
            onClick={handleNextClientInfo}
            disabled={!client[IS_CLIENT_FORM_VALID]}
            sx={{ ml: 3 }}
          >
            submit & next
          </Button>
        </>
      )}
    </>
  );
}

export default ClientCurrent;
